import { call } from '@redux-saga/core/effects';
import type { ReadAdvancedSearchResultsApiResponse } from 'lib-shared-types-api';
import mailtoParser from 'mailto-parser';
import { put, select, takeEvery } from 'typed-redux-saga';
import isEmail from 'validator/lib/isEmail';

import markdownToHTML from 'app/utils/markdownToHTML';
import {
  isNumberValid,
  numberFormatterInternational,
} from 'app/utils/telephonyHelpers';
import { AppState } from 'bootstrap.store';
import { notificationActions } from 'components/notifications/actions/notification';
// eslint-disable-next-line no-restricted-imports
import { composeEmailActions } from 'components/shared/composeEmail/actions/composeEmailActions';
// eslint-disable-next-line no-restricted-imports
import { findContact } from 'components/shared/composeEmail/api/composeEmail';
import * as telephonyActions from 'components/telephony/actions/telephony';

import { electronActions } from './electron.actions';

export const electronSagas = [
  takeEvery(electronActions.makeCall.type, makeCallSaga),
  takeEvery(electronActions.composeEmail.type, composeEmailSaga),
];

/**
 * Attempts to dispatch an action that opens the telephony dropdown and makes
 * the call or an error notification if something goes wrong.
 *
 * @param action The action.
 */
export function* makeCallSaga(
  action: ReturnType<typeof electronActions.makeCall>,
): Generator<unknown> {
  try {
    const { isReady, isMicrophoneBlocked } = yield* select(
      (state: AppState) => state.telephony,
    );
    if (!isReady || isMicrophoneBlocked) {
      yield dispatchErrorNotification(
        !isReady ? 'The phone is not ready yet' : 'The microphone is blocked',
      );
    } else if (action.payload.phoneNumber) {
      const phoneNumber = decodeURIComponent(action.payload.phoneNumber);
      const truncatedPhoneNumber = phoneNumber.replace(/ /g, '');
      if (isNumberValid({ phoneNumber: truncatedPhoneNumber })) {
        const internationalPhoneNumber = numberFormatterInternational({
          phoneNumber: truncatedPhoneNumber,
        });

        yield put(telephonyActions.actions.telephonyOpenDropdown());
        yield put(
          telephonyActions.actions.telephonySetSearchValue(
            internationalPhoneNumber,
          ),
        );
      } else {
        yield dispatchErrorNotification(
          `The phone number ${truncatedPhoneNumber} is not valid`,
        );
      }
    } else {
      yield dispatchErrorNotification(
        `The phone number ${action.payload.phoneNumber} is not valid`,
      );
    }
  } catch (error) {
    yield dispatchErrorNotification('Could not make an outbound call');
  }
}

/**
 * Attempts to dispatch an action that opens up the modal for composing an email
 * or an error notification if something goes wrong.
 *
 * @param action The action.
 */
export function* composeEmailSaga(
  action: ReturnType<typeof electronActions.composeEmail>,
): Generator<unknown> {
  try {
    if (action.payload.mailto) {
      const { addresses, attributeKey } = mailtoParser.parse(
        action.payload.mailto,
      );

      for (const address of addresses) {
        if (!isEmail(address)) {
          yield dispatchErrorNotification(`The email ${address} is not valid`);
        }
      }

      const address = addresses.find((address) => isEmail(address));

      let toUser: { user: { email?: string; id?: string } };

      if (address) {
        toUser = {
          user: {
            email: address,
          },
        };

        // The email might already be assigned to a user, so we should try to get that user.
        try {
          // Not possible to add correct return type from yield call.
          const response = yield call(findContact, {
            params: {
              scope: 'users',
              query: address,
              strict: true,
            },
          });

          if (
            (response as ReadAdvancedSearchResultsApiResponse)?.data?.results
              ?.length
          ) {
            const userHit = (
              response as ReadAdvancedSearchResultsApiResponse
            )?.data.results.find((hit) => hit.hits_type === 'user');

            if (userHit?.hits?.length) {
              toUser = {
                user: userHit.hits[0],
              };
            }
          }
        } catch (err) {
          console.log(err);
        }

        yield put(
          composeEmailActions.composeEmailOpenModal({
            initialValues: {
              toUser: [toUser],
              ...(attributeKey.subject && {
                subject: decodeURIComponent(attributeKey.subject),
              }),
              ...(attributeKey.body && {
                message: markdownToHTML(decodeURIComponent(attributeKey.body)),
              }),
              ...(attributeKey.cc &&
                isEmail(attributeKey.cc) && {
                  cc: [{ user: { id: null, email: attributeKey.cc } }],
                }),
            },
          }),
        );
      } else {
        yield dispatchErrorNotification('Could not find valid email');
      }
    } else {
      yield dispatchErrorNotification('Could not find email');
    }
  } catch (error) {
    yield dispatchErrorNotification('Could not compose email');
    console.warn(`${composeEmailSaga.name} error`, error);
  }
}

/**
 * Dispatches a notification with an error text. Note: This should probably be
 * moved to a central place in case there are more consumers.
 *
 * @param text The error message.
 */
function* dispatchErrorNotification(text: string) {
  yield put(
    notificationActions.notificationAdd({
      title: 'An error occurred',
      className: 'info',
      icon: 'i-info',
      text,
    }),
  );
}
